import { useQueryClient } from '@tanstack/react-query';

import { DashboardT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { getChartData } from 'routes/Explore/hooks/reducers/helpers';
import { SettingsState } from 'routes/Explore/hooks/reducers/settingsReducer';
import { useSaveChartFromData } from 'routes/Explore/hooks/saveChartFromData';
import { useSaveChartToDashboard } from 'routes/Explore/hooks/saveChartToDashboard';
import useDuplicateChart from 'hooks/useDuplicateChart';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'routes/AllDashboards/hooks/getDashboardsList';

export const useAddChartToDashboard = () => {
    const { saveChartFromData } = useSaveChartFromData();
    const { saveChartToDashboard } = useSaveChartToDashboard();
    const queryClient = useQueryClient();

    const updateDashboardOnSuccess = (
        key: number,
        updatedProps: Partial<DashboardT>,
    ) => {
        queryClient.setQueryData(
            [ACTIVE_DASHBOARDS_QUERY_KEY],
            (oldDashboards: DashboardT[]) =>
                oldDashboards.map((dashboard) =>
                    dashboard.key === key
                        ? { ...dashboard, updatedProps }
                        : dashboard,
                ),
        );
        queryClient.invalidateQueries({
            queryKey: ['getLayout', updatedProps.slug, ''],
        });
    };

    const duplicateChartMutation = useDuplicateChart(updateDashboardOnSuccess);

    const addChartToDashboard = (
        explore: ExploreQueryT,
        dashboardElement?: DashboardT,
        chartKey?: number | null,
        widgetKey?: number | null,
        isInDashboard?: boolean,
        query?: ExploreQueryT,
        settings?: Pick<
            SettingsState,
            'chartTitle' | 'selectedViz' | 'chartSettings'
        >,
        redirect: boolean = false,
        snackAction: boolean = true,
        fromPosition?: FromPositionQueryT,
    ) => {
        if (dashboardElement && dashboardElement.key) {
            const { key, slug } = dashboardElement;
            if (chartKey !== undefined && chartKey !== null) {
                if (isInDashboard && widgetKey) {
                    duplicateChartMutation.mutate({
                        widgetKey,
                        dashboardSlug: slug,
                        fromPosition,
                    });
                } else {
                    saveChartToDashboard({
                        dashboard: { key, slug },
                        explore,
                        redirect,
                        snackAction,
                        fromPosition,
                    });
                }
            } else if (query !== undefined && settings !== undefined) {
                saveChartFromData({
                    dashboard: { key, slug },
                    chartData: getChartData(query, settings),
                    redirect,
                    snackAction,
                    fromPosition,
                });
                trackEvent(
                    'decision/indicatorlibrary/opendata-and-models/saved-to-dashboard',
                    {
                        indicator: chartKey,
                    },
                );
            }
        }
    };
    return { addChartToDashboard };
};
